import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore } from '../store/authStore';

type AuthMode = 'login' | 'register' | 'forgot-password';

export default function LoginPage() {
    const navigate = useNavigate();
    const { isAuthenticated, loginWithGitHub, signUp, signInWithEmail, resetPassword } = useAuthStore();
    const [mode, setMode] = useState<AuthMode>('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/', { replace: true });
        }
    }, [isAuthenticated, navigate]);

    const validateEmail = (email: string) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const validatePassword = (password: string) => {
        return password.length >= 6;
    };

    const handleGitHubLogin = async () => {
        setIsLoading(true);
        setError(null);

        try {
            await loginWithGitHub();
            // Redirect will happen automatically via Supabase OAuth
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : '登录失败，请稍后重试';
            setError(message);
            setIsLoading(false);
        }
    };

    const handleEmailSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccessMessage(null);

        // Validation
        if (!validateEmail(email)) {
            setError('请输入有效的邮箱地址');
            return;
        }

        if (mode !== 'forgot-password' && !validatePassword(password)) {
            setError('密码至少需要 6 个字符');
            return;
        }

        if (mode === 'register' && password !== confirmPassword) {
            setError('两次输入的密码不一致');
            return;
        }

        setIsLoading(true);

        try {
            if (mode === 'login') {
                await signInWithEmail(email, password);
                navigate('/', { replace: true });
            } else if (mode === 'register') {
                const result = await signUp(email, password);
                if (result.needsConfirmation) {
                    setSuccessMessage('注册成功！请查收确认邮件以完成注册。');
                    setMode('login');
                } else {
                    navigate('/', { replace: true });
                }
            } else if (mode === 'forgot-password') {
                await resetPassword(email);
                setSuccessMessage('重置密码邮件已发送，请查收邮箱。');
                setMode('login');
            }
        } catch (err: unknown) {
            let message = '操作失败，请稍后重试';
            if (err instanceof Error) {
                // Translate common Supabase error messages
                if (err.message.includes('Invalid login credentials')) {
                    message = '邮箱或密码错误';
                } else if (err.message.includes('Email not confirmed')) {
                    message = '邮箱尚未验证，请查收确认邮件';
                } else if (err.message.includes('User already registered')) {
                    message = '该邮箱已被注册';
                } else if (err.message.includes('Password should be at least')) {
                    message = '密码至少需要 6 个字符';
                } else {
                    message = err.message;
                }
            }
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleGuestPlay = () => {
        navigate('/levels');
    };

    const switchMode = (newMode: AuthMode) => {
        setMode(newMode);
        setError(null);
        setSuccessMessage(null);
    };

    const getModeTitle = () => {
        switch (mode) {
            case 'login': return '登录';
            case 'register': return '注册';
            case 'forgot-password': return '重置密码';
        }
    };

    const getModeDescription = () => {
        switch (mode) {
            case 'login': return '使用邮箱或 GitHub 账号登录';
            case 'register': return '创建新账号';
            case 'forgot-password': return '输入邮箱地址，我们将发送重置密码链接';
        }
    };

    return (
        <div className="min-h-[70vh] flex items-center justify-center py-6 sm:py-12 px-3 sm:px-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="glass-card p-5 sm:p-8 w-full max-w-md"
            >
                <div className="text-center mb-6 sm:mb-8">
                    <img
                        src="/sudoku.svg"
                        alt="疯狂的数独"
                        className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-3 sm:mb-4"
                    />
                    <h1 className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2">{getModeTitle()}</h1>
                    <p className="text-sm sm:text-base text-gray-500">{getModeDescription()}</p>
                </div>

                {error && (
                    <div className="mb-4 sm:mb-6 p-3 sm:p-4 rounded-lg bg-red-100 border border-red-300 text-red-600 text-sm">
                        {error}
                    </div>
                )}

                {successMessage && (
                    <div className="mb-4 sm:mb-6 p-4 sm:p-5 rounded-lg bg-green-50 border border-green-300 text-center">
                        <div className="text-3xl mb-2">📧</div>
                        <div className="text-green-700 font-medium mb-1">{successMessage}</div>
                        {successMessage.includes('确认邮件') && (
                            <p className="text-green-600 text-xs sm:text-sm mt-2">
                                请打开您的邮箱，点击邮件中的确认链接完成注册。
                                <br />
                                <span className="text-gray-500">如未收到邮件，请检查垃圾邮件文件夹。</span>
                            </p>
                        )}
                    </div>
                )}

                {/* Email Form */}
                <form onSubmit={handleEmailSubmit} className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-600 mb-1.5 sm:mb-2">
                            邮箱
                        </label>
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="your@email.com"
                            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-white border border-gray-300 text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors text-base"
                            disabled={isLoading}
                            required
                        />
                    </div>

                    {mode !== 'forgot-password' && (
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-600 mb-1.5 sm:mb-2">
                                密码
                            </label>
                            <input
                                type="password"
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="至少 6 个字符"
                                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-white border border-gray-300 text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors text-base"
                                disabled={isLoading}
                                required
                                minLength={6}
                            />
                        </div>
                    )}

                    {mode === 'register' && (
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-600 mb-1.5 sm:mb-2">
                                确认密码
                            </label>
                            <input
                                type="password"
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="再次输入密码"
                                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-white border border-gray-300 text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors text-base"
                                disabled={isLoading}
                                required
                                minLength={6}
                            />
                        </div>
                    )}

                    {mode === 'login' && (
                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={() => switchMode('forgot-password')}
                                className="text-sm text-green-600 hover:text-green-500 transition-colors touch-manipulation"
                            >
                                忘记密码？
                            </button>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full btn-primary flex items-center justify-center gap-2 py-2.5 sm:py-3 text-sm sm:text-base touch-manipulation"
                    >
                        {isLoading ? (
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <span>
                                {mode === 'login' && '登录'}
                                {mode === 'register' && '注册'}
                                {mode === 'forgot-password' && '发送重置邮件'}
                            </span>
                        )}
                    </button>
                </form>

                {/* Mode Switch */}
                <div className="text-center text-sm text-gray-500 mb-4 sm:mb-6">
                    {mode === 'login' && (
                        <>
                            还没有账号？{' '}
                            <button
                                onClick={() => switchMode('register')}
                                className="text-green-600 hover:text-green-500 transition-colors touch-manipulation"
                            >
                                立即注册
                            </button>
                        </>
                    )}
                    {mode === 'register' && (
                        <>
                            已有账号？{' '}
                            <button
                                onClick={() => switchMode('login')}
                                className="text-green-600 hover:text-green-500 transition-colors touch-manipulation"
                            >
                                立即登录
                            </button>
                        </>
                    )}
                    {mode === 'forgot-password' && (
                        <button
                            onClick={() => switchMode('login')}
                            className="text-green-600 hover:text-green-500 transition-colors touch-manipulation"
                        >
                            返回登录
                        </button>
                    )}
                </div>

                {/* Divider */}
                {mode !== 'forgot-password' && (
                    <>
                        <div className="relative my-4 sm:my-6">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-300"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-4 bg-white text-gray-500">或</span>
                            </div>
                        </div>

                        {/* GitHub Login */}
                        <button
                            onClick={handleGitHubLogin}
                            disabled={isLoading}
                            className="w-full flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-xl text-white font-medium transition-all bg-[#24292F] hover:bg-[#24292F]/90 disabled:opacity-50 disabled:cursor-not-allowed mb-3 sm:mb-4 text-sm sm:text-base touch-manipulation"
                        >
                            {isLoading ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0 1 12 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" />
                                </svg>
                            )}
                            <span>GitHub 登录</span>
                        </button>

                        {/* Guest Play */}
                        <button
                            onClick={handleGuestPlay}
                            className="w-full btn-secondary flex items-center justify-center gap-2 py-2.5 sm:py-3 text-sm sm:text-base touch-manipulation"
                        >
                            <span>🎮</span>
                            <span>以游客身份试玩</span>
                        </button>

                        <p className="mt-4 sm:mt-6 text-center text-xs sm:text-sm text-gray-500">
                            游客模式下游戏进度仅保存在本地
                        </p>
                    </>
                )}
            </motion.div>
        </div>
    );
}
